/**
 * Newsletter Form Constants
 *
 * Shared constants for the newsletter subscription form.
 *
 * Country list uses the comprehensive ISO 3166 set accepted by the
 * Salesforce/backend `CountryCode` enum (see `./countries.ts`). This is
 * intentionally broader than the sales-enabled markets in
 * `lib/configuration/fallback.ts`, because anyone worldwide can
 * subscribe to the newsletter regardless of whether we ship there.
 *
 * Language list matches the i18n locales supported by the storefront
 * (see messages/*.json).
 */

import { localeConfig } from '@/lib/configuration/configuration';
import { NEWSLETTER_COUNTRIES } from './countries';

/**
 * Language options for the newsletter preference selector.
 * Must stay in sync with supported storefront locales in messages/*.json.
 */
export const NEWSLETTER_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' },
  { value: 'ko', label: '한국어' },
  { value: 'ru', label: 'Русский' },
  { value: 'zht', label: '繁體中文' },
] as const;

type NewsletterLanguageOption = (typeof NEWSLETTER_LANGUAGE_OPTIONS)[number];

// Locale slugs that differ from their NEWSLETTER_LANGUAGE_OPTIONS value.
// "jp" is the URL slug for Japanese; the newsletter option uses "ja".
const SLUG_TO_NEWSLETTER_VALUE: Record<string, string> = {
  jp: 'ja',
};

export function normalizeLanguageSlug(slug: string): string {
  return SLUG_TO_NEWSLETTER_VALUE[slug] ?? slug;
}

/**
 * Returns the subset of NEWSLETTER_LANGUAGE_OPTIONS relevant to a given
 * country code, derived from that country's configured locales. Falls back
 * to English only when no locales match (never reverts to all 11 options).
 */
export function getLanguageOptionsForCountry(
  countryCode: string
): readonly NewsletterLanguageOption[] {
  const country = localeConfig.getCountryByCode(countryCode);
  if (!country) {
    return NEWSLETTER_LANGUAGE_OPTIONS.filter((o) => o.value === 'en');
  }

  const newsletterValues = new Set(country.locales.map((l) => normalizeLanguageSlug(l.slug)));

  const filtered = NEWSLETTER_LANGUAGE_OPTIONS.filter((o) => newsletterValues.has(o.value));
  return filtered.length > 0
    ? filtered
    : NEWSLETTER_LANGUAGE_OPTIONS.filter((o) => o.value === 'en');
}

/**
 * Country options shown in the newsletter form.
 *
 * Sourced from the comprehensive ISO 3166 list in `./countries.ts` so
 * visitors from any country (e.g. Taiwan, Finland, Sweden, South Africa,
 * Middle East countries) can subscribe. Sorted alphabetically by label.
 */
export const NEWSLETTER_COUNTRY_OPTIONS = [...NEWSLETTER_COUNTRIES]
  .sort((a, b) => a.label.localeCompare(b.label))
  .map((c) => ({ value: c.value, label: c.label }));

/**
 * Brand-specific configuration for newsletter legal/display text.
 */
export const NEWSLETTER_BRAND_CONFIG = {
  gallet: {
    displayName: 'Gallet',
    privacyEmail: 'dataprivacy@gallet.com',
  },
  breitling: {
    displayName: 'Breitling',
    privacyEmail: 'dataprivacy@breitling.com',
  },
  ug: {
    displayName: 'Universal Genève',
    privacyEmail: 'dataprivacy@universalgeneve.com',
  },
} as const;

/**
 * Default mailing preferences sent during guest newsletter confirmation.
 * These match the categories used by the MyAccount App.
 */
export const NEWSLETTER_MAILING_PREFERENCES = {
  gallet: [
    'Novelties',
    'Squad Updates',
    'News About Gallet',
    'Gallet Events',
    'Special Editions',
    'Gallet Trade',
  ],
  breitling: [
    'Novelties',
    'Squad Updates',
    'News About Breitling',
    'Breitling Events',
    'Special Editions',
    'Breitling Trade',
  ],
  ug: ['Novelties', 'News About Universal Genève', 'Universal Genève Events', 'Special Editions'],
} as const;
